'use client'

import classnames from 'classnames'
import styles from './GiftCardGrid.module.scss'
import GiftCardForm, { CardMetadata, CardType } from '@/components/GiftCardForm/GiftCardForm'
import SanityImage from '@/components/SanityImage/SanityImage'
import { useState, useEffect, useMemo } from 'react'
import Modal from '@/components/Modal/Modal'
import Toast from '@/components/Toast/Toast'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Navigation } from 'swiper/modules'
import Select from '@/components/Select/Select'
import 'swiper/css'
import 'swiper/css/navigation'
import useI18n from '@/hooks/use-i18n'
import useCurrentPage from '@/hooks/use-current-page'
import Toggle from '@/components/Toggle/Toggle'
import { getCuratedAvailableCategories } from '@/utils/giftCardHelpers'

const GiftCardGrid = ({ categories: curatedCategories }: SanityGiftCardGrid) => {
  const { i18n } = useI18n()
  const { currentLanguage } = useCurrentPage()
  const [showModal, setShowModal] = useState(false)
  const [showToast, setShowToast] = useState(false)
  const [selectedCategory, setSelectedCategory] = useState(i18n('allCards'))
  const [selectedCard, setSelectedCard] = useState<CardMetadata | undefined>()
  const [cardType, setCardType] = useState<{ id: CardType; text: string }>()
  const cardInfoByType = useMemo(
    () =>
      ({
        digital: { id: 'digital', text: i18n('egiftCards') },
        physical: { id: 'physical', text: i18n('physicalCards') },
      }) as const,
    [i18n],
  )

  useEffect(() => {
    const hash = window.location.hash
    if (hash === '#physical') {
      setCardType(cardInfoByType.physical)
    } else {
      setCardType(cardInfoByType.digital)
    }
  }, [cardInfoByType])

  const categories = useMemo(() => {
    if (!cardType || !curatedCategories || !currentLanguage) return []
    const regionCode = (currentLanguage as string).replace('-', '_')
    return getCuratedAvailableCategories(curatedCategories, cardType.id, regionCode)
  }, [cardType, curatedCategories, currentLanguage])

  const categoriesToShow = useMemo(() => {
    return selectedCategory === i18n('allCards')
      ? categories
      : categories.filter(category => category.title === selectedCategory)
  }, [categories, selectedCategory, i18n])

  if (!curatedCategories) return null

  const handleModalOpen = (card: CardMetadata) => () => {
    setSelectedCard(card)
    setShowModal(true)
  }

  const handleFormSubmit = () => {
    setShowModal(false)
    setShowToast(true)
  }

  const handleToastClose = () => {
    setShowToast(false)
  }

  const handleToggleCardType = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const newCardType = e.target.value
    setSelectedCategory(i18n('allCards'))
    if (newCardType === i18n('physicalCards')) {
      setCardType(cardInfoByType.physical)
      window.history.replaceState(null, '', '#physical')
    } else {
      setCardType(cardInfoByType.digital)
      window.history.replaceState(null, '', window.location.pathname)
    }
  }

  return (
    <div className={styles.GiftCardGrid}>
      {showModal && selectedCard && cardType && (
        <Modal onClose={() => setShowModal(false)}>
          <GiftCardForm
            cardType={cardType.id}
            faceplateCode={selectedCard.faceplateCode}
            image={selectedCard.image}
            onSubmit={handleFormSubmit}
          />
        </Modal>
      )}

      <Toast
        message={i18n('giftCardAddedToCart')}
        isVisible={showToast}
        onClose={handleToastClose}
      />

      <div className={styles.inner}>
        <Toggle
          items={[cardInfoByType.digital.text, cardInfoByType.physical.text]}
          name="cardType"
          value={cardType?.text}
          onChange={handleToggleCardType}
          className={styles.toggle}
        />

        <div className={styles.titleContainer}>
          <h3>{cardType?.text}</h3>
          <Select
            name="selectedCategory"
            value={selectedCategory}
            onChange={e => setSelectedCategory(e.target.value)}
            items={[i18n('allCards'), ...categories.map(category => category.title)]}
            className={styles.selectedCategory}
          />
        </div>

        {!categories?.length && cardType && (
          <div className={styles.noCardsAvailableContainer}>
            {cardType?.id === 'physical' ? i18n('noPhysicalGiftCardsAvailable') : i18n('noDigitalGiftCardsAvailable')}
          </div>
        )}

        {categoriesToShow?.map(({ title, cards, id }) => (
          <div
            key={id}
            className={styles.category}
          >
            <h4>{title}</h4>
            <div className={styles.slider}>
              <Swiper
                className={styles.swiper}
                modules={[Navigation]}
                spaceBetween={16}
                centeredSlides={false}
                navigation={{
                  nextEl: `.${styles.swiperButtonNext}-${id}`,
                  prevEl: `.${styles.swiperButtonPrev}-${id}`,
                }}
                allowTouchMove
                grabCursor
                slidesPerView="auto"
                breakpoints={{ 768: { spaceBetween: 24 } }}
              >
                {cards.map(card => (
                  <SwiperSlide
                    key={card.id}
                    className={styles.swiperSlide}
                  >
                    <div
                      className={styles.card}
                      onClick={handleModalOpen(card)}
                    >
                      <SanityImage
                        image={card.image}
                        className={styles.image}
                      />
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
              <button
                className={classnames(styles.swiperButton, styles.swiperButtonPrev, `${styles.swiperButtonPrev}-${id}`)}
                type="button"
                aria-label={i18n('ariaPreviousCardFromCategory', { category: title })}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M15 18L9 12L15 6"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              <button
                className={classnames(styles.swiperButton, styles.swiperButtonNext, `${styles.swiperButtonNext}-${id}`)}
                type="button"
                aria-label={i18n('ariaNextSlideFromCategory', { category: title })}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M9 6L15 12L9 18"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

GiftCardGrid.displayName = 'GiftCardGrid'

export default GiftCardGrid
